import React, { useRef, useState } from 'react';
import * as Helper from '../../../util/Helper';
import TableHorizontal, { InfoObject, Label } from '../../misc/TableHorizontal';
import { Button, UncontrolledTooltip } from 'reactstrap';
import ModalComponent from '../../misc/ModalComponent';
import StatusCode from '../../../util/StatusCode';
import { Link } from 'react-router-dom';
import { RequestState } from '../../../models/RequestState';
import { CoreTracking, CoreTrackingModule, SourceObjectOwnership, TrackingOwnership } from '../../../models/CoreTracking';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrackingOperations from './TrackingOperations';
import { useTranslation } from 'react-i18next';
import  TrackingsNavigation  from './TrackingsNavigation'; 
import { FlowSyncObjectType } from '../../../models/CoreModels';

interface TrackingsModuleProps {
    trackings: RequestState<CoreTracking[]>
    noResMsg: string
    onDataInvalidated?: () => void
    flowSyncObjectType?: FlowSyncObjectType
}

const DateColumn: React.FC<{ date: string }> = props => {
    var ref = useRef(null);
    var formattedDateOnly = Helper.formatDateOnly(props.date);
    return <>
        <span className="text-nowrap" ref={ref}>
            {formattedDateOnly}
        </span>
        {formattedDateOnly !== '-' &&
            <UncontrolledTooltip target={ref} autohide={false} placement="top-start" className="mr-3" onClick={e => e.stopPropagation()}>
                {Helper.formatDate(props.date)}
            </UncontrolledTooltip>
        }
    </>;
}

const compareTrackingModules = (a: CoreTrackingModule, b: CoreTrackingModule) => {
    if (a.active && !b.active) {
        return -1;
    } else if (b.active && !a.active) {
        return 1;
    } else {
        return b.trackingOwnership - a.trackingOwnership;
    }
}

const compareTrackings = (a: CoreTracking, b: CoreTracking) => {
    if (a.active && !b.active) {
        return -1;
    } else if (b.active && !a.active) {
        return 1;
    } else {
        return b.flowSyncObjectOwnership - a.flowSyncObjectOwnership;
    }
}


const TrackingsModule: React.FC<TrackingsModuleProps> = (props: TrackingsModuleProps) => {
    const { t } = useTranslation(['common', 'schoolAdministration']);
    const [open, setOpen] = useState(false);
    const [metaData, setMetaData] = useState<string | undefined>(undefined);

    const invalidateDataOnSuccess = (success: boolean) => {
        if (success && props.onDataInvalidated) {
            props.onDataInvalidated();
        }
    }

    const renderModuleList = (modules: CoreTrackingModule[]) => modules
        .sort(compareTrackingModules)
        .map((module) => {
            const iconKey = `statusIcon_${module.moduleId}`;

            let icon: JSX.Element;
            if (!module.active) {
                icon = (<FontAwesomeIcon key={iconKey} title={t('trackingModule.iconTitle.inactive', { ns: 'schoolAdministration' })} color="gray" icon={['far', 'circle']} />);
            } else if (module.trackingOwnership === TrackingOwnership.PRIMARY) {
                icon = (<FontAwesomeIcon key={iconKey} title={t('trackingModule.iconTitle.owner', { ns: 'schoolAdministration' })} color="green" icon={['fas', 'check-circle']} />);
            } else if (module.trackingOwnership === TrackingOwnership.POTENTIAL) {
                icon = (<FontAwesomeIcon key={iconKey} title={t('trackingModule.iconTitle.potentialOwner', { ns: 'schoolAdministration' })} color="green" icon={['far', 'check-circle']} />);
            } else {
                icon = (<FontAwesomeIcon key={iconKey} title={t('trackingModule.iconTitle.couldNotOwn', { ns: 'schoolAdministration' })} color="green" icon={['far', 'circle']} />);
            }

            const link = (<Link key={module.moduleId} to={`/module/${module.moduleId}/status`}>{module.moduleName}</Link>);

            return <div><span className="me-2">{icon}</span> {link}</div>;
        });

    const trackingTableHeader: Label[] = [
        { value: t('trackingModule.trackingTableHeader.source', { ns: 'schoolAdministration' }) },
        { value: t('trackingModule.trackingTableHeader.sourceEnvironment', { ns: 'schoolAdministration' }) },
        { value: t('trackingModule.trackingTableHeader.sourceObjectType', { ns: 'schoolAdministration' }) },
        { value: t('trackingModule.trackingTableHeader.sourceSystemId', { ns: 'schoolAdministration' }) },
        { value: t('trackingModule.trackingTableHeader.status', { ns: 'schoolAdministration' }) },
        { value: t('genericTableHeaders.created', { ns: 'common' }) },
        { value: t('genericTableHeaders.modified', { ns: 'common' }) },
    ]

    const getTrackingStatus = (tracking: CoreTracking) => {
        if (tracking.sourceObjectOwnership === SourceObjectOwnership.PRIMARY) {
            let statusSuffix = '';
            if (!tracking.active) {
                statusSuffix = `, ${t('trackingModule.trackingStatus.disabled', { ns: 'schoolAdministration' })}`;
            }
            if (tracking.deleted !== undefined) {
                if (tracking.active) {
                    statusSuffix = `, ${t('trackingModule.trackingStatus.deletedNotDisabled', { ns: 'schoolAdministration' })}`;
                }
                return t('trackingModule.trackingStatus.deleted', { ns: 'schoolAdministration' }) + statusSuffix
            }
            if (tracking.archived !== undefined) {
                return t('trackingModule.trackingStatus.archived', { ns: 'schoolAdministration' }) + statusSuffix
            }
            if (tracking.inactivated !== undefined) {
                return t('trackingModule.trackingStatus.waitingOnArchive', { ns: 'schoolAdministration' }) + statusSuffix
            }
        }
        if (tracking.active) {
            return t('genericTableHeaders.active', { ns: 'common' });
        }
        return t('trackingModule.trackingStatus.inactive', { ns: 'schoolAdministration' });
    }

    const mapTrackings = (): InfoObject[] => {
        if (props.trackings.code !== StatusCode.COMPLETE) {
            return [];
        }

        const infoObjects = props.trackings.data
            .sort(compareTrackings)
            .map<InfoObject>((tracking) => ({
                id: tracking.trackingId,
                data: [
                    { value: tracking.sourceName },
                    { value: tracking.sourceEnvironment },
                    { value: tracking.sourceObjectType },
                    { value: <TrackingsNavigation Tracking={tracking}/> },
                    { value: getTrackingStatus(tracking) },
                    { value: <DateColumn date={tracking.created} /> },
                    { value: <DateColumn date={tracking.modified} /> },
                ],
                expandableContent: (<div className="pb-3">
                    <TableHorizontal
                        className='m-0 mini'
                        header={[
                            { value: t('trackingModule.expandableTrackingTableHeader.modules', {ns: 'schoolAdministration'}) },
                            { value: t('trackingModule.expandableTrackingTableHeader.metaData', { ns: 'schoolAdministration' }) },
                            { value: t('trackingModule.expandableTrackingTableHeader.inActivated', { ns: 'schoolAdministration' }) },
                            { value: t('trackingModule.expandableTrackingTableHeader.archived', { ns: 'schoolAdministration' }) },
                            { value: t('trackingModule.expandableTrackingTableHeader.removed', { ns: 'schoolAdministration' }) },
                            { value: t('trackingModule.expandableTrackingTableHeader.disabled', { ns: 'schoolAdministration' }) },
                            { value: t('trackingModule.expandableTrackingTableHeader.nextAnalysis', { ns: 'schoolAdministration' }), className: "text-nowrap" },
                            { value: t('trackingModule.expandableTrackingTableHeader.alternative', { ns: 'schoolAdministration' }) },
                        ]}
                        info={[{
                            id: '0',
                            data: [
                                { value: renderModuleList(tracking.modules) },
                                { value: tracking.metadata ? <Button size="sm" title={t('trackingModule.showMetaData', { ns: 'schoolAdministration' })} className="text-nowrap" onClick={() => (setOpen(true), setMetaData(tracking.metadata))}>{t('trackingModule.showMetaData', { ns: 'schoolAdministration' })}</Button> : '-' },
                                { value: <DateColumn date={tracking.inactivated} /> },
                                { value: <DateColumn date={tracking.archived} /> },
                                { value: <DateColumn date={tracking.deleted} /> },
                                { value: <DateColumn date={tracking.disabled} /> },
                                { value: <DateColumn date={tracking.nextUpdateCheck} /> },
                                { value: <TrackingOperations tracking={tracking} onClose={invalidateDataOnSuccess} flowSyncObjectType={props.flowSyncObjectType} /> }
                            ]
                        }]}
                        searching={false}
                        error={false}
                    />
                </div>)
        }))

        return infoObjects;
    }

    return (<div>
        <h5 className="header-menu">{t('trackingModule.sources', { ns: 'schoolAdministration' })}</h5>

        <TableHorizontal
            header={trackingTableHeader}
            info={mapTrackings()}
            searching={props.trackings.code === StatusCode.PENDING}
            error={props.trackings.code === StatusCode.ERROR}
            noResMsg={props.noResMsg}
        />

        {open && metaData !== undefined &&
            <ModalComponent
                isOpen={open}
                toggleModal={() => (setOpen(false), setMetaData(undefined))}
                header={t('trackingModule.debugInformation', { ns: 'schoolAdministration' })}
                size="xl"
                cancel={t('button.close', { ns: 'common' })}
            >
                <div>
                    <pre>{Helper.formatText(metaData)}</pre>
                </div>
            </ModalComponent>}
    </div>);
}

export default TrackingsModule;
